import * as React from "react";
import { Link } from "react-router-dom";
import { useAction } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { WORKFORCE_PERSONAS, AgentRoutine } from "../../../../../convex/aiWorkforce/config";
import { getPersonaTheme, formatToolkitName } from "./personaTheme";
import { AgentRoutinesModal } from "./AgentRoutinesModal";
import { WorkDeliverableCard } from "./WorkDeliverableCard";
import { DocumentDeliverableCanvas } from "./canvases/DocumentDeliverableCanvas";
import { MarkdownDeliverableCanvas } from "./canvases/MarkdownDeliverableCanvas";
import {
  FileText,
  ShieldAlert,
  CheckCircle2,
  XCircle,
  Building2,
  Users,
  Clock,
  Sparkles,
  ExternalLink,
  X,
  History,
  Check,
  ShieldCheck,
  Bot,
  Plug,
  Calendar,
  Plus,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";

interface Pane3Props {
  channel?: any;
  channelArtifacts?: any[];
  activeArtifact?: any;
  onSelectArtifact?: (artifactId: string) => void;
  pendingApprovals: any[];
  approvalHistory?: any[];
  onApprove: (approvalId: string) => Promise<void>;
  onReject: (approvalId: string, reason?: string) => Promise<void>;
  onApproveArtifact?: (artifactId: string) => Promise<void>;
  onClose?: () => void;
}

const ACTION_TYPE_LABELS: Record<string, string> = {
  create_work_order: "Work Order Draft",
  dispatch_standby: "Standby Cleaner Dispatch",
  sync_crm_leads: "CRM Leads Sync",
  generate_invoice: "Invoice Draft",
  send_client_message: "Client Outreach Draft",
  update_shift_coverage: "Shift Coverage Update",
};

export function Pane3SidekickCanvas({
  channel,
  channelArtifacts = [],
  activeArtifact,
  onSelectArtifact,
  pendingApprovals = [],
  approvalHistory = [],
  onApprove,
  onReject,
  onApproveArtifact,
  onClose,
}: Pane3Props) {
  const [activeTab, setActiveTab] = React.useState<"specialists" | "work" | "approvals">("specialists");
  const [processingApprovalId, setProcessingApprovalId] = React.useState<string | null>(null);
  const [connections, setConnections] = React.useState<any[]>([]);
  const [isRoutinesModalOpen, setIsRoutinesModalOpen] = React.useState(false);
  const listConnectionsAction = useAction(api.connections.list);

  // Routine toggles state
  const [enabledRoutines, setEnabledRoutines] = React.useState<Record<string, boolean>>(() => {
    const saved = localStorage.getItem("mc_agent_routines_state");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {}
    }
    return {};
  });

  // Schedule overrides state
  const [scheduleOverrides, setScheduleOverrides] = React.useState<Record<string, string>>(() => {
    const saved = localStorage.getItem("mc_routine_schedule_overrides");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {}
    }
    return {};
  });

  const toggleRoutine = (routineId: string, defaultVal = false) => {
    setEnabledRoutines((prev) => {
      const isCurrentlyEnabled = prev[routineId] !== undefined ? prev[routineId] : defaultVal;
      const next = { ...prev, [routineId]: !isCurrentlyEnabled };
      localStorage.setItem("mc_agent_routines_state", JSON.stringify(next));
      return next;
    });
  };

  React.useEffect(() => {
    let isMounted = true;
    listConnectionsAction({})
      .then((res) => {
        if (isMounted) setConnections(res || []);
      })
      .catch(() => {});
    return () => {
      isMounted = false;
    };
  }, [listConnectionsAction]);

  const connectedSlugs = React.useMemo(() => {
    return new Set(
      connections
        .filter((c) => c.active)
        .map((c) => (c.platform || c.name || "").toLowerCase())
    );
  }, [connections]);

  // If an active work item is selected, auto-switch to work tab
  React.useEffect(() => {
    if (activeArtifact) {
      setActiveTab("work");
    }
  }, [activeArtifact?._id]);

  const handleApprove = async (approvalId: string) => {
    setProcessingApprovalId(approvalId);
    try {
      await onApprove(approvalId);
    } finally {
      setProcessingApprovalId(null);
    }
  };

  const handleApproveDirect = async (artifactId: string) => {
    setProcessingApprovalId(artifactId);
    try {
      if (onApproveArtifact) {
        await onApproveArtifact(artifactId);
      }
    } finally {
      setProcessingApprovalId(null);
    }
  };

  const handleReject = async (approvalId: string, reason?: string) => {
    setProcessingApprovalId(approvalId);
    try {
      await onReject(approvalId, reason);
    } finally {
      setProcessingApprovalId(null);
    }
  };

  const isDM = channel?.type === "dm" || (channel?.participantPersonas?.length === 1);
  const singlePersonaKey = channel?.participantPersonas?.[0] || "chief_of_staff";
  const singlePersona = WORKFORCE_PERSONAS[singlePersonaKey] || WORKFORCE_PERSONAS.chief_of_staff;

  // Participant personas in this channel
  const participantPersonas = (channel?.participantPersonas || ["chief_of_staff"])
    .map((key: string) => WORKFORCE_PERSONAS[key])
    .filter(Boolean);

  const pendingCount = pendingApprovals.length;
  const artifactCount = channelArtifacts.length;

  const agentRoutines: AgentRoutine[] = singlePersona.routines || [];

  return (
    <div className="w-full h-full min-h-0 min-w-0 bg-white rounded-2xl border border-gray-200 shadow-sm flex flex-col overflow-hidden select-none animate-in fade-in duration-150">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-white min-w-0 flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          {isDM ? (
            <div
              className={`w-8 h-8 rounded-lg ${getPersonaTheme(singlePersona.key).bgLight} ${getPersonaTheme(singlePersona.key).text} flex items-center justify-center flex-shrink-0 border ${getPersonaTheme(singlePersona.key).border}`}
            >
              <Bot className="w-4 h-4" />
            </div>
          ) : (
            <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
          )}
          <div className="min-w-0 truncate">
            <span className="text-base font-bold text-gray-900 truncate block">
              {isDM
                ? `${singlePersona.name} (${singlePersona.title})`
                : channel?.title
                ? `${channel.title} Details`
                : "Channel Details"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
              title="Close Panel"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="grid grid-cols-3 border-b border-gray-200 bg-gray-50/80 p-1.5 gap-1.5 text-sm font-semibold flex-shrink-0">
        <button
          onClick={() => setActiveTab("specialists")}
          className={`py-2 px-2 rounded-xl transition-all flex items-center justify-center gap-2 whitespace-nowrap text-sm ${
            activeTab === "specialists"
              ? "bg-white text-blue-700 shadow-xs border border-gray-200 font-bold"
              : "text-gray-600 hover:text-gray-900 hover:bg-white/60"
          }`}
        >
          {isDM ? <Bot className="w-4 h-4 flex-shrink-0" /> : <Users className="w-4 h-4 flex-shrink-0" />}
          <span>{isDM ? "Profile" : "Team"}</span>
        </button>

        <button
          onClick={() => setActiveTab("work")}
          className={`py-2 px-2 rounded-xl transition-all flex items-center justify-center gap-2 whitespace-nowrap text-sm ${
            activeTab === "work"
              ? "bg-white text-blue-700 shadow-xs border border-gray-200 font-bold"
              : "text-gray-600 hover:text-gray-900 hover:bg-white/60"
          }`}
        >
          <FileText className="w-4 h-4 flex-shrink-0" />
          <span>Work</span>
          {artifactCount > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 font-bold">
              {artifactCount}
            </span>
          )}
        </button>

        <button
          onClick={() => setActiveTab("approvals")}
          className={`py-2 px-2 rounded-xl transition-all flex items-center justify-center gap-2 whitespace-nowrap text-sm ${
            activeTab === "approvals"
              ? "bg-white text-blue-700 shadow-xs border border-gray-200 font-bold"
              : "text-gray-600 hover:text-gray-900 hover:bg-white/60"
          }`}
        >
          <History className="w-4 h-4 flex-shrink-0" />
          <span>Approvals</span>
          {pendingCount > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-bold animate-pulse">
              {pendingCount}
            </span>
          )}
        </button>
      </div>

      {/* Tab Body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-5 custom-scrollbar bg-slate-50/40 min-w-0">
        {/* TAB 1: TEAM MEMBER / SPECIALISTS PROFILE */}
        {activeTab === "specialists" && (
          <div className="space-y-4 min-w-0">
            {isDM ? (
              // 1:1 PROFILE VIEW
              <div className="space-y-4">
                {/* Profile Card Header */}
                <div className="p-4 rounded-xl bg-white border border-gray-200 shadow-xs space-y-3.5">
                  <div className="flex items-center gap-3.5">
                    <div
                      className={`w-12 h-12 rounded-2xl ${getPersonaTheme(singlePersona.key).bgLight} border ${getPersonaTheme(singlePersona.key).border} ${getPersonaTheme(singlePersona.key).text} flex items-center justify-center flex-shrink-0 shadow-xs`}
                    >
                      <Bot className="w-6 h-6" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="text-lg font-bold text-gray-900 truncate block">
                        {singlePersona.name}
                      </span>
                      <p className="text-sm font-medium text-gray-500 mt-0.5 truncate">
                        {singlePersona.title}
                      </p>
                    </div>
                  </div>

                  {/* Role Description */}
                  <div className="pt-3 border-t border-gray-100">
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1.5 flex items-center gap-1.5">
                      <ShieldCheck className="w-4 h-4 text-blue-600" />
                      <span>Role & Responsibilities</span>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {singlePersona.description}
                    </p>
                  </div>
                </div>

                {/* Scheduled Routines */}
                <div className="p-4 rounded-xl bg-white border border-gray-200 shadow-xs space-y-3.5">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
                      <Calendar className="w-4 h-4 text-blue-600" />
                      <span>Scheduled Routines</span>
                    </div>
                    <button
                      onClick={() => setIsRoutinesModalOpen(true)}
                      className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1"
                    >
                      <span>Configure</span>
                    </button>
                  </div>

                  {agentRoutines.length === 0 ? (
                    <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 text-center space-y-1.5">
                      <p className="text-sm font-semibold text-gray-700">No pre-set routines</p>
                      <p className="text-xs text-gray-500">
                        {singlePersona.name} is ready for on-demand tasks.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2.5">
                      {agentRoutines.map((routine) => {
                        const isEnabled = enabledRoutines[routine.id] !== undefined ? enabledRoutines[routine.id] : (routine.defaultEnabled ?? false);
                        return (
                          <div
                            key={routine.id}
                            className={`p-3.5 rounded-xl border transition-all space-y-2 ${
                              isEnabled
                                ? "bg-white border-blue-200 shadow-2xs"
                                : "bg-gray-50/70 border-gray-200 opacity-60"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-bold text-gray-900 text-sm">
                                {routine.name}
                              </span>
                              <Switch
                                checked={isEnabled}
                                onCheckedChange={() => toggleRoutine(routine.id)}
                              />
                            </div>

                            <p className="text-xs text-gray-600 leading-relaxed">
                              {routine.description}
                            </p>

                            <div className="flex items-center justify-between pt-1 border-t border-gray-100 text-xs">
                              <span className="inline-flex items-center gap-1 text-gray-700 font-semibold">
                                <Clock className="w-3.5 h-3.5 text-blue-600" />
                                {scheduleOverrides[routine.id] || routine.scheduleLabel}
                              </span>
                              <span className="text-blue-700 font-semibold text-2xs truncate max-w-[140px]" title={routine.outputDeliverable}>
                                📄 {routine.outputDeliverable}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Add Custom Routine Button */}
                  <div className="pt-1">
                    <button
                      onClick={() => setIsRoutinesModalOpen(true)}
                      className="w-full py-2.5 px-3 rounded-xl border border-dashed border-gray-300 hover:border-blue-500 hover:bg-blue-50/50 text-xs font-semibold text-gray-600 hover:text-blue-700 flex items-center justify-center gap-1.5 transition-all shadow-2xs"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>Manage & Add Custom Routines</span>
                    </button>
                  </div>
                </div>

                {/* Recommended Connections */}
                <div className="bg-white rounded-xl p-4 border border-gray-200 space-y-3">
                  <div className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center justify-between">
                    <span className="flex items-center gap-1.5">
                      <Plug className="w-4 h-4 text-blue-600" />
                      <span>Recommended Connections</span>
                    </span>
                    <Link
                      to="/settings/connections"
                      className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-0.5"
                    >
                      <span>Manage</span>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </Link>
                  </div>

                  {(() => {
                    const agentToolkits = singlePersona.composioToolkits || [];
                    if (agentToolkits.length === 0) {
                      return (
                        <p className="text-xs text-gray-500 italic mt-0.5">
                          Connected directly to your ManageClean database.
                        </p>
                      );
                    }

                    return (
                      <div className="flex flex-wrap gap-1.5 pt-0.5">
                        {agentToolkits.map((slug) => {
                          const isConnected = connectedSlugs.has(slug.toLowerCase());
                          const label = formatToolkitName(slug);
                          return (
                            <Link
                              key={slug}
                              to="/settings/connections"
                              title={isConnected ? `${label}: Connected` : `${label}: Not connected (Click to configure)`}
                              className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg border font-medium transition-all ${
                                isConnected
                                  ? "bg-emerald-50 text-emerald-900 border-emerald-200 hover:bg-emerald-100 shadow-2xs"
                                  : "bg-gray-100/90 text-gray-600 border-gray-200 hover:bg-gray-200/70"
                              }`}
                            >
                              <span
                                className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                  isConnected ? "bg-emerald-500 ring-2 ring-emerald-200" : "bg-gray-400"
                                }`}
                              />
                              <span>{label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              </div>
            ) : (
              // CHANNEL SPECIALISTS LIST
              <div className="space-y-3.5 min-w-0">
                <div className="text-xs font-bold uppercase tracking-wider text-gray-400">
                  Assigned to this Channel
                </div>

                <div className="space-y-3">
                  {participantPersonas.map((persona: any) => {
                    const theme = getPersonaTheme(persona.key);
                    const routinesCount = (persona.routines || []).length;

                    return (
                      <div
                        key={persona.key}
                        className="p-4 rounded-xl bg-white border border-gray-200 shadow-xs space-y-2.5 min-w-0"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className={`w-10 h-10 rounded-xl ${theme.bgLight} border ${theme.border} ${theme.text} flex items-center justify-center flex-shrink-0 shadow-2xs`}
                          >
                            <Bot className="w-5 h-5" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-1">
                              <span className="text-base font-bold text-gray-900 truncate">
                                {persona.name}
                              </span>
                              <Badge variant="outline" className="text-xs font-medium text-gray-600 bg-gray-50">
                                {persona.title.replace(" Agent", "")}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-emerald-700 flex items-center gap-1.5 font-semibold">
                                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                                Ready
                              </span>
                              {routinesCount > 0 && (
                                <span className="text-xs text-blue-700 font-medium">
                                  • {routinesCount} Routine{routinesCount > 1 ? "s" : ""}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <p className="text-sm text-gray-600 leading-relaxed pt-2 border-t border-gray-100">
                          {persona.description}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB 2: WORK & DRAFTS */}
        {activeTab === "work" && (
          <div className="space-y-4 min-w-0">
            {/* Active / Selected Work Item Preview */}
            {activeArtifact && (() => {
              const matchingApproval = pendingApprovals.find(
                (a) => a.artifactId === activeArtifact._id || (a as any).relatedArtifactId === activeArtifact._id
              );
              const isPdfDoc =
                activeArtifact.type === "document_pdf" ||
                activeArtifact.type === "commercial_proposal" ||
                Boolean(activeArtifact.data?.pdfUrl) ||
                Boolean(activeArtifact.data?.typstSource);
              const isMarkdownDoc =
                activeArtifact.type === "markdown_report" ||
                activeArtifact.type === "analytical_report" ||
                activeArtifact.type === "markdown" ||
                Boolean(activeArtifact.data?.markdown);

              return (
                <div className="space-y-3 pb-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-wider text-blue-700 flex items-center gap-1.5">
                      <Sparkles className="w-4 h-4 text-blue-600" /> Selected Preview
                    </span>
                    <Badge
                      variant="outline"
                      className={`text-xs font-bold ${
                        activeArtifact.status === "approved"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : activeArtifact.status === "pending_approval"
                          ? "bg-amber-50 text-amber-700 border-amber-200"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {activeArtifact.status === "pending_approval"
                        ? "PENDING REVIEW"
                        : activeArtifact.status.toUpperCase()}
                    </Badge>
                  </div>

                  {/* PDF Document Canvas */}
                  {isPdfDoc ? (
                    <DocumentDeliverableCanvas
                      artifact={activeArtifact}
                      onApprove={
                        matchingApproval
                          ? () => handleApprove(matchingApproval._id)
                          : () => handleApproveDirect(activeArtifact._id)
                      }
                      onReject={matchingApproval ? () => handleReject(matchingApproval._id) : undefined}
                    />
                  ) : isMarkdownDoc ? (
                    /* Markdown Canvas */
                    <MarkdownDeliverableCanvas
                      artifact={activeArtifact}
                      onApprove={
                        matchingApproval
                          ? () => handleApprove(matchingApproval._id)
                          : () => handleApproveDirect(activeArtifact._id)
                      }
                      onReject={matchingApproval ? () => handleReject(matchingApproval._id) : undefined}
                    />
                  ) : (
                    /* Interactive Canvas Cards */
                    <div className="p-4 rounded-xl bg-white border border-blue-200 space-y-3.5 shadow-sm min-w-0">
                      <div>
                        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2 truncate">
                          <Building2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
                          <span className="truncate">{activeArtifact.title}</span>
                        </h3>
                        <span className="text-xs text-gray-500 font-medium mt-0.5 block truncate">
                          Prepared by @{WORKFORCE_PERSONAS[activeArtifact.createdPersona]?.name || activeArtifact.createdPersona}
                        </span>
                      </div>

                      {/* Lead Batch Data */}
                      {activeArtifact.type === "lead_batch_preview" && activeArtifact.data && (
                        <div className="space-y-3 text-sm min-w-0">
                          <div className="grid grid-cols-2 gap-2.5">
                            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200 min-w-0">
                              <span className="text-gray-500 block text-xs font-bold uppercase tracking-wider">Leads</span>
                              <span className="text-base sm:text-lg font-bold text-gray-900 mt-0.5 block">
                                {activeArtifact.data.totalFound || activeArtifact.data.leads?.length || 0}
                              </span>
                            </div>
                            <div className="p-3 rounded-lg bg-emerald-50/60 border border-emerald-200 min-w-0">
                              <span className="text-emerald-700 block text-xs font-bold uppercase tracking-wider">Pipeline</span>
                              <span className="text-base sm:text-lg font-bold text-emerald-800 truncate block mt-0.5">
                                {activeArtifact.data.estPipelineValue || "$0"}
                              </span>
                            </div>
                          </div>

                          {activeArtifact.data.leads && (
                            <div className="space-y-2 min-w-0">
                              <span className="text-xs uppercase font-bold text-gray-500 tracking-wider block">
                                Prospects
                              </span>
                              <div className="space-y-2 max-h-48 overflow-y-auto">
                                {activeArtifact.data.leads.map((lead: any, idx: number) => (
                                  <div
                                    key={idx}
                                    className="p-2.5 rounded-lg bg-gray-50 border border-gray-200 text-sm space-y-0.5 min-w-0"
                                  >
                                    <div className="font-semibold text-gray-900 truncate text-sm">{lead.name}</div>
                                    <div className="text-gray-500 text-xs truncate">{lead.contact || lead.contactName || lead.phone || ""}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Work Order Draft Data */}
                      {activeArtifact.type === "work_order_draft" && activeArtifact.data && (
                        <div className="space-y-2 text-sm min-w-0">
                          <div className="p-3.5 rounded-lg bg-gray-50 border border-gray-200 space-y-2 text-sm min-w-0">
                            {activeArtifact.data.siteName && (
                              <div className="flex items-center justify-between">
                                <span className="text-gray-500 text-sm">Site: </span>
                                <span className="font-bold text-gray-900 text-sm">
                                  {activeArtifact.data.siteName}
                                </span>
                              </div>
                            )}
                            {activeArtifact.data.scheduledTime && (
                              <div className="flex items-center justify-between">
                                <span className="text-gray-500 text-sm">Shift: </span>
                                <span className="text-gray-800 text-sm">{activeArtifact.data.scheduledTime}</span>
                              </div>
                            )}
                            {activeArtifact.data.standbyStaff && (
                              <div className="flex items-center justify-between">
                                <span className="text-gray-500 text-sm">Assigned Cleaner: </span>
                                <span className="text-blue-700 font-bold text-sm">
                                  {activeArtifact.data.standbyStaff}
                                </span>
                              </div>
                            )}
                            {activeArtifact.data.reason && (
                              <div className="flex items-center justify-between">
                                <span className="text-gray-500 text-sm">Reason: </span>
                                <span className="text-gray-700 text-sm italic">
                                  {activeArtifact.data.reason}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Invoice Preview Data */}
                      {activeArtifact.type === "invoice_preview" && activeArtifact.data && (
                        <div className="space-y-2 text-sm min-w-0">
                          <div className="p-3.5 rounded-lg bg-gray-50 border border-gray-200 space-y-2 text-sm min-w-0">
                            <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                              <span className="text-gray-500 font-bold uppercase text-xs">Invoice #{activeArtifact.data.invoiceNumber || "Draft"}</span>
                              <span className="font-bold text-emerald-700 text-base">{activeArtifact.data.totalAmount || activeArtifact.data.amountDue || "$0.00"}</span>
                            </div>
                            {activeArtifact.data.clientName && (
                              <div className="flex items-center justify-between">
                                <span className="text-gray-500 text-sm">Client: </span>
                                <span className="font-semibold text-gray-900 text-sm">{activeArtifact.data.clientName}</span>
                              </div>
                            )}
                            {activeArtifact.data.dueDate && (
                              <div className="flex items-center justify-between">
                                <span className="text-gray-500 text-sm">Due: </span>
                                <span className="text-gray-700 text-sm">{activeArtifact.data.dueDate}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* HR Onboarding Packet Data */}
                      {activeArtifact.type === "hr_onboarding_packet" && activeArtifact.data && (
                        <div className="space-y-2 text-sm min-w-0">
                          <div className="p-3.5 rounded-lg bg-gray-50 border border-gray-200 space-y-2 text-sm min-w-0">
                            <div className="font-bold text-gray-900 text-base">{activeArtifact.data.candidateName || activeArtifact.data.employeeName || "New Hire"}</div>
                            {activeArtifact.data.role && (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-500">Role: </span>
                                <span className="font-semibold text-gray-800">{activeArtifact.data.role}</span>
                              </div>
                            )}
                            {Array.isArray(activeArtifact.data.checklist) && (
                              <div className="space-y-1.5 pt-2 border-t border-gray-200">
                                <span className="text-gray-500 font-bold uppercase block text-xs">Checklist:</span>
                                {activeArtifact.data.checklist.map((item: string, i: number) => (
                                  <div key={i} className="flex items-center gap-1.5 text-sm text-gray-700">
                                    <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                                    <span>{item}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Client Message Draft */}
                      {activeArtifact.type === "client_message_draft" && activeArtifact.data && (
                        <div className="space-y-2 text-sm min-w-0">
                          <div className="p-3.5 rounded-lg bg-gray-50 border border-gray-200 space-y-2 text-sm min-w-0">
                            <div className="font-bold text-gray-900 text-base">{activeArtifact.data.subject || "Client Message Draft"}</div>
                            {activeArtifact.data.recipient && (
                              <div className="text-gray-500 text-xs">To: {activeArtifact.data.recipient}</div>
                            )}
                            <p className="text-gray-700 whitespace-pre-wrap pt-2 border-t border-gray-200 leading-relaxed text-sm">
                              {activeArtifact.data.body || activeArtifact.data.message}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Action Approval Bar if pending */}
                      {activeArtifact.status === "pending_approval" && (
                        <div className="pt-3 border-t border-gray-200 space-y-2">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                if (matchingApproval) {
                                  handleApprove(matchingApproval._id);
                                } else {
                                  handleApproveDirect(activeArtifact._id);
                                }
                              }}
                              disabled={Boolean(processingApprovalId)}
                              className="flex-1 py-2.5 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold flex items-center justify-center gap-2 transition-all shadow-xs disabled:opacity-50 cursor-pointer"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                              <span>Approve</span>
                            </button>
                            {matchingApproval && (
                              <button
                                onClick={() => handleReject(matchingApproval._id)}
                                disabled={Boolean(processingApprovalId)}
                                className="py-2.5 px-3 rounded-lg bg-white hover:bg-gray-100 border border-gray-300 text-gray-700 text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50 cursor-pointer"
                              >
                                <XCircle className="w-4 h-4 text-rose-500" />
                                <span>Decline</span>
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* List of all Work in Channel */}
            <div className="space-y-2.5">
              <div className="text-xs font-bold uppercase tracking-wider text-gray-400">
                Channel Work ({channelArtifacts.length})
              </div>

              {channelArtifacts.length === 0 ? (
                <div className="p-6 rounded-xl bg-white border border-gray-200 text-center space-y-2 shadow-xs">
                  <Sparkles className="w-6 h-6 mx-auto text-blue-500" />
                  <p className="font-bold text-gray-800 text-base">No work generated yet</p>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    When the team creates lead lists, schedules, or invoices in this channel, they will appear here.
                  </p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {channelArtifacts.map((art) => (
                    <WorkDeliverableCard
                      key={art._id}
                      artifact={art}
                      isSelected={activeArtifact?._id === art._id}
                      compact={true}
                      onOpen={() => onSelectArtifact?.(art._id)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 3: APPROVALS */}
        {activeTab === "approvals" && (
          <div className="space-y-4 min-w-0">
            {/* Pending Approvals Actions */}
            {pendingApprovals.length > 0 && (
              <div className="space-y-3">
                <span className="text-xs font-bold uppercase tracking-wider text-amber-800 flex items-center gap-1.5">
                  <ShieldAlert className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  Requires Sign-off ({pendingApprovals.length})
                </span>

                <div className="space-y-3">
                  {pendingApprovals.map((approval) => (
                    <div
                      key={approval._id}
                      className="p-4 rounded-xl bg-amber-50 border border-amber-200 shadow-xs space-y-3"
                    >
                      <div className="flex items-start justify-between gap-1 min-w-0">
                        <span className="text-base font-bold text-gray-900 truncate">
                          {ACTION_TYPE_LABELS[approval.actionType] || approval.actionType.replace(/_/g, " ")}
                        </span>
                      </div>

                      <p className="text-sm text-gray-700 leading-relaxed">
                        Requested by <span className="font-bold text-blue-700">@{WORKFORCE_PERSONAS[approval.requestedByPersona]?.name || approval.requestedByPersona}</span>.
                      </p>

                      <div className="flex items-center gap-3 pt-1">
                        <button
                          onClick={() => handleApprove(approval._id)}
                          disabled={processingApprovalId === approval._id}
                          className="flex-1 py-2.5 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold flex items-center justify-center gap-2 transition-all shadow-xs disabled:opacity-50"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Approve</span>
                        </button>
                        <button
                          onClick={() => handleReject(approval._id)}
                          disabled={processingApprovalId === approval._id}
                          className="py-2.5 px-4 rounded-lg bg-white hover:bg-gray-100 border border-gray-300 text-gray-700 text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50"
                        >
                          <XCircle className="w-4 h-4 text-rose-500" />
                          <span>Decline</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Historical Audit Trail */}
            <div className="space-y-2.5">
              <span className="text-xs font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
                <History className="w-4 h-4 text-blue-600 flex-shrink-0" />
                Approval History
              </span>

              {approvalHistory.length === 0 ? (
                <div className="p-6 rounded-xl bg-white border border-gray-200 text-center space-y-2 shadow-xs">
                  <p className="font-bold text-gray-700 text-base">No past decisions recorded</p>
                  <p className="text-sm text-gray-500">Actions approved or declined will appear here.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {approvalHistory.map((item) => {
                    const isAppr = item.status === "approved";
                    const isRej = item.status === "rejected";

                    return (
                      <div
                        key={item._id}
                        className="p-3.5 rounded-xl bg-white border border-gray-200 space-y-2 shadow-xs min-w-0"
                      >
                        <div className="flex items-center justify-between gap-1">
                          <span className="text-sm font-bold text-gray-900 truncate">
                            {ACTION_TYPE_LABELS[item.actionType] || item.actionType.replace(/_/g, " ")}
                          </span>
                          <Badge
                            variant="outline"
                            className={`text-xs font-bold uppercase flex items-center gap-1 flex-shrink-0 ${
                              isAppr
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                : isRej
                                ? "bg-rose-50 text-rose-700 border-rose-200"
                                : "bg-amber-50 text-amber-700 border-amber-200"
                            }`}
                          >
                            {isAppr && <Check className="w-3.5 h-3.5" />}
                            {isRej && <X className="w-3.5 h-3.5" />}
                            {item.status === "rejected" ? "Declined" : item.status}
                          </Badge>
                        </div>

                        <div className="text-sm text-gray-500 flex items-center justify-between">
                          <span>By @{WORKFORCE_PERSONAS[item.requestedByPersona]?.name || item.requestedByPersona}</span>
                          <span>{new Date(item.reviewedAt || item.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>

                        {item.artifactTitle && (
                          <div className="text-sm text-blue-700 font-medium truncate pt-1 border-t border-gray-100">
                            📄 {item.artifactTitle}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Direct 1-Click Agent Routines Modal */}
      <AgentRoutinesModal
        isOpen={isRoutinesModalOpen}
        onClose={() => setIsRoutinesModalOpen(false)}
        personaKey={singlePersonaKey}
      />
    </div>
  );
}
